import React, { useEffect, useState } from "react";
import { Table, Modal, Badge, Card } from "react-bootstrap";
import axios from "axios";
import { FaMapMarkerAlt, FaCubes } from "react-icons/fa";

function BasicExample() {
  const [sortedClientiData, setSortedClientiData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "valore", direction: "descending" });
  const [showModal, setShowModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get("mancata_vendita_dashboard.json");
        const clientiData = res.data?.TP?.localita || [];

        const transformedData = clientiData.reduce((acc, item) => {
          const id     = item.id_cliente;
          // "origine" non esiste nel json, usiamo Tipo Documento come fallback
          const origine = item.origine || item["Tipo Documento"] || "web";
          const key    = `${id}-${origine}`;

          // ── Mapping esplicito JSON → colonne ──────────────────────────────
          // "Valore"            → colonna VALORE
          // "Valore_Last_Minute"→ colonna LAST MINUTE
          // "Valore_Normale"    → colonna REGULAR
          const parseVal = (v) => parseFloat(String(v ?? "0").replace(",", ".")) || 0;

          const valoreTotale     = parseVal(item.Valore);             // VALORE
          const valoreLastMinute = parseVal(item.Valore_Last_Minute); // LAST MINUTE
          const valoreNormale    = parseVal(item.Valore_Normale);     // REGULAR

          if (!acc[key]) {
            acc[key] = {
              id_cliente: id,
              cliente:    item.clienti,
              localita:   item.regione || item.nazione || item.paese || "—",
              origine,
              valore:   0, // VALORE totale
              express:  0, // LAST MINUTE
              regular:  0, // REGULAR
              numero:   0,
              dettagli: [],
            };
          }

          acc[key].valore  += valoreTotale;
          acc[key].express += valoreLastMinute;
          acc[key].regular += valoreNormale;
          acc[key].numero  += Number(item.numero_doc || 0);
          acc[key].dettagli.push({
            descrizione: item.descrizione,
            note:        item.note_agg,
            valore:      valoreTotale,
          });

          return acc;
        }, {});

        setSortedClientiData(
          sortArray(Object.values(transformedData), sortConfig.key, sortConfig.direction)
        );
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [sortConfig]);

  const sortArray = (array, key, direction) =>
    [...array].sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      if (typeof av === "number")
        return direction === "ascending" ? av - bv : bv - av;
      return direction === "ascending"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

  const handleSort = (key) =>
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "ascending"
          ? "descending"
          : "ascending",
    });

  const formatToEuro = (amount) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) return null;
    return sortConfig.direction === "ascending" ? " ↑" : " ↓";
  };

  return (
    <>
      <Card
        className="card ombra ordini-ora card-home hover-effect border-0"
        style={{ borderRadius: "15px", padding: "1.2rem", paddingBottom: "0" }}
      >
        <div
          className="no-scrollbar"
          style={{ width: "100%", height: 300, overflowY: "auto", overflowX: "auto", position: "relative" }}
        >
          <Table
            hover
            className="mb-0 custom-table"
            style={{ fontSize: "0.75rem", width: "100%", minWidth: "700px", margin: 0, borderCollapse: "separate" }}
          >
            <thead className="sticky-top bg-white" style={{ zIndex: 10 }}>
              <tr className="text-muted small" style={{ borderBottom: "2px solid #f1f1f1" }}>
                <th style={{ width: "25%" }} className="ps-4 py-3 border-0">CLIENTE</th>
                <th style={{ width: "15%" }} className="py-3 border-0 cursor-pointer" onClick={() => handleSort("localita")}>
                  LOCALITÀ<SortIcon colKey="localita" />
                </th>
                <th style={{ width: "15%" }} className="py-3 border-0 text-end cursor-pointer" onClick={() => handleSort("valore")}>
                  VALORE<SortIcon colKey="valore" />
                </th>
                <th style={{ width: "15%" }} className="py-3 border-0 text-end text-danger cursor-pointer" onClick={() => handleSort("express")}>
                  LAST MINUTE<SortIcon colKey="express" />
                </th>
                <th style={{ width: "12%" }} className="py-3 border-0 text-end text-secondary cursor-pointer" onClick={() => handleSort("regular")}>
                  REGULAR<SortIcon colKey="regular" />
                </th>
                <th style={{ width: "8%" }} className="py-3 border-0 text-center">ORDINI</th>
                <th style={{ width: "10%" }} className="py-3 border-0 text-center pe-4">ORIGINE</th>
              </tr>
            </thead>
            <tbody>
              {sortedClientiData.map((item, index) => (
                <tr
                  key={index}
                  onClick={() => { setSelectedCliente(item); setShowModal(true); }}
                  className="cursor-pointer align-middle"
                >
                  <td className="ps-4 fw-bold text-dark truncate-cell">{item.cliente}</td>
                  <td className="text-muted truncate-cell" style={{ fontSize: "0.7rem" }}>{item.localita}</td>

                  {/* VALORE = Valore (totale) */}
                  <td className="text-end fw-bold text-dark no-wrap">{formatToEuro(item.valore)}</td>

                  {/* LAST MINUTE = Valore_Last_Minute */}
                  <td className="text-end text-danger fw-bold no-wrap">{formatToEuro(item.express)}</td>

                  {/* REGULAR = Valore_Normale */}
                  <td className="text-end text-secondary no-wrap" style={{ fontSize: "0.65rem" }}>{formatToEuro(item.regular)}</td>

                  <td className="text-center"><span className="text-dark">{item.numero}</span></td>
                  <td className="text-center pe-4">
                    <Badge
                      pill
                      className={item.origine === "web" ? "bg-danger-subtle text-danger" : "bg-secondary-subtle text-secondary"}
                      style={{ fontSize: "0.6rem" }}
                    >
                      {item.origine.toUpperCase()}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <style>{`
          .custom-table { width: 100% !important; margin: 0 !important; }
          .truncate-cell { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 150px; }
          .no-wrap { white-space: nowrap; }
          .cursor-pointer { cursor: pointer; }
          .fw-800 { font-weight: 800; }
          tbody tr:hover { background-color: #fbfbfb !important; }
          .sticky-top { top: -1px; background: white !important; box-shadow: 0 2px 2px -1px rgba(0,0,0,0.05); }
          ::-webkit-scrollbar { height: 6px; width: 4px; }
          ::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 10px; }
        `}</style>
      </Card>

      {/* MODAL DETTAGLIO */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="sm"
        backdrop={true}
      >
        <Modal.Header closeButton className="border-0 pb-3 pt-4 px-4">
          <Modal.Title className="d-flex align-items-center w-100">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm"
              style={{ width: "45px", height: "45px", border: "1px solid rgba(0,0,0,0.3)" }}
            >
              <FaCubes size={22} style={{ color: "rgb(48,48,48)" }} />
            </div>
            <div className="overflow-hidden">
              <div style={{ fontSize: "1.25rem", fontWeight: "800", letterSpacing: "-0.5px", lineHeight: "1.2" }}>
                {selectedCliente?.cliente}
              </div>
              <div className="text-muted fw-medium d-flex align-items-center mt-1" style={{ fontSize: "0.85rem" }}>
                <FaMapMarkerAlt className="me-1 text-danger" />
                <span>{selectedCliente?.localita}</span>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 pb-4 pt-0">
          <div className="mb-4 d-flex gap-2 flex-wrap">
            <Badge bg="secondary" className="bg-opacity-25 text-dark fw-normal border border-secondary" style={{ fontSize: "0.7rem", padding: "5px 10px" }}>
              ID: {selectedCliente?.id_cliente}
            </Badge>
            <Badge pill className="bg-info bg-opacity-10 text-info border border-info" style={{ fontSize: "0.7rem", padding: "5px 10px" }}>
              {selectedCliente?.origine?.toUpperCase()}
            </Badge>
            {/* KPI veloci */}
            <Badge pill className="bg-danger bg-opacity-10 text-danger border border-danger" style={{ fontSize: "0.7rem", padding: "5px 10px" }}>
              LM: {formatToEuro(selectedCliente?.express)}
            </Badge>
            <Badge pill className="bg-secondary bg-opacity-10 text-secondary border border-secondary" style={{ fontSize: "0.7rem", padding: "5px 10px" }}>
              REG: {formatToEuro(selectedCliente?.regular)}
            </Badge>
          </div>

          <div className="rounded-3 overflow-hidden">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th className="ps-3 py-3 text-uppercase" style={{ fontSize: "0.7rem", color: "#353535", letterSpacing: "1px" }}>Brand / Nota</th>
                  <th className="text-end pe-3 py-3 text-uppercase" style={{ fontSize: "0.7rem", color: "#353535", letterSpacing: "1px" }}>Valore</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: "1px solid #333" }}>
                {selectedCliente?.dettagli.map((d, i) => (
                  <tr key={i} className="align-middle" style={{ borderBottom: "1px solid #e4e4e4" }}>
                    <td className="ps-3 py-2 fw-medium border-0" style={{ fontSize: "0.9rem" }}>
                      {d.descrizione || "Generico"}
                      {d.note && <small className="d-block text-muted" style={{ fontSize: "0.68rem" }}>{d.note}</small>}
                    </td>
                    <td className="text-end pe-3 py-2 fw-bold border-0" style={{ fontSize: "0.95rem" }}>
                      {formatToEuro(d.valore)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="ps-3 py-3 border-0 fw-bold" style={{ fontSize: "0.9rem" }}>TOTALE</td>
                  <td className="text-end pe-3 py-3 border-0 fw-800" style={{ fontSize: "1.1rem" }}>
                    {formatToEuro(selectedCliente?.valore)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </Modal.Body>

        <style>{`.fw-800 { font-weight: 800; }`}</style>
      </Modal>
    </>
  );
}

export default BasicExample;